import { REGEX_PHONE_NUMBER } from "constants/common/common";
import {
  SOCreateFormValues,
  SOItemFormValues,
  SOPartnerFormValues,
} from "constants/types/salesOrder";
import dayjs from "configs/dayjs";
import * as yup from "yup";

export type { SOCreateFormValues, SOItemFormValues, SOPartnerFormValues };

export const generateRowKey = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const emptyItem = (): SOItemFormValues => ({
  rowKey: generateRowKey(),
  productId: "",
  productName: "",
  productSpec: "",
  productUnit: "",
  quantity: 1,
  initialPrice: 0,
  actualPrice: 0,
});

export const emptyPartner = (): SOPartnerFormValues => ({
  id: "",
  name: "",
  phone: "",
  taxCode: "",
  contactPersonId: "",
  regionId: "",
  notes: "",
});

export const initialValues: SOCreateFormValues = {
  code: "",
  warehouseId: "",
  notes: "",
  partner: emptyPartner(),
  items: [emptyItem()],
  oldDebt: 0,
  createdAt: dayjs().unix(),
};

export const createValidationSchema = yup.object().shape({
  code: yup.string().optional(),
  warehouseId: yup.string().required("Vui lòng chọn kho"),
  notes: yup.string().optional(),
  oldDebt: yup
    .number()
    .typeError("Vui lòng nhập số hợp lệ")
    .min(0, "Công nợ cũ không được âm")
    .optional(),
  createdAt: yup
    .number()
    .typeError("Vui lòng chọn ngày tạo")
    .min(0)
    .required("Vui lòng chọn ngày tạo"),
  partner: yup.object().shape({
    id: yup.string(),
    name: yup
      .string()
      .min(2, "Tên phải có ít nhất 2 ký tự")
      .required("Vui lòng nhập tên khách hàng"),
    phone: yup
      .string()
      .matches(REGEX_PHONE_NUMBER, {
        message: "Số điện thoại không hợp lệ",
        excludeEmptyString: true,
      })
      .optional(),
    taxCode: yup.string().optional(),
    contactPersonId: yup.string().optional(),
    // Khi chọn đối tác có sẵn (partner.id có giá trị) thì form khoá các ô này lại
    // (disabled={isExisting}) và backend cũng bỏ qua chúng — resolvePartner nạp
    // đối tác theo id rồi lờ mọi field còn lại. Bắt buộc chúng lúc đó sẽ tạo ra
    // ô vừa trống, vừa bị khoá, vừa chặn submit: nút bấm không phản ứng gì mà
    // người dùng không sửa được. Chỉ bắt buộc khi đang tạo đối tác mới.
    regionId: yup
      .string()
      .when("id", {
        is: (id?: string) => !id,
        then: (schema) => schema.required("Vui lòng chọn địa bàn"),
        otherwise: (schema) => schema.optional(),
      }),
    notes: yup.string().optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        productId: yup.string().required("Vui lòng chọn sản phẩm"),
        // Người dùng sửa được tên này (chỉ áp cho đơn hiện tại), nên bắt buộc
        // phải kèm message — required() trống sẽ chặn submit mà không hiện gì.
        productName: yup
          .string()
          .trim()
          .required("Vui lòng nhập tên sản phẩm"),
        productUnit: yup.string().required("Sản phẩm chưa có đơn vị tính"),
        quantity: yup
          .number()
          .typeError("Vui lòng nhập số lượng")
          .positive("Số lượng phải lớn hơn 0")
          .required("Vui lòng nhập số lượng"),
        initialPrice: yup
          .number()
          .typeError("Vui lòng nhập giá gốc")
          .min(0, "Giá gốc không được âm")
          .required("Vui lòng nhập giá gốc"),
        actualPrice: yup
          .number()
          .typeError("Vui lòng nhập giá bán")
          .min(0, "Giá bán không được âm")
          .required("Vui lòng nhập giá bán"),
      }),
    )
    .min(1, "Cần ít nhất 1 sản phẩm"),
});
